import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useConversation } from '@elevenlabs/react';
import { startTurnHold } from '../utils/turnHold';
import { intakeApi, STATUS_LABELS, BUSY_MESSAGE, isBusyError } from '../utils/jobIntake';

export const WRAP_UP_AFTER_S = 15 * 60;
export const END_AFTER_QUIET_MS = 3000;
export const END_MAX_WAIT_MS = 60000;
export const TALENT_POLL_MS = 20000;               // talent sources not ready at call start: check every 20 s …
export const TALENT_POLL_MAX = 30;                 // … for at most 10 minutes
const WRAP_UP_NOTE = 'Time check: about 15 minutes in. Finish the remaining topics briefly, then call request_finish.';

// useConversation lives in a component that NEVER re-renders (claude.md): a re-render during the live
// WebRTC session would rebuild the ElevenLabs connection. The config object is created once (useRef).
const ConversationManager = React.memo(({ config, conversationRef, isSpeakingRef }) => {
  const conv = useConversation(config);
  conversationRef.current = conv;
  isSpeakingRef.current = conv.isSpeaking;
  return null;
}, () => true);

/**
 * After the server approves Jessica's finish: end the call once she has spoken her goodbye and been quiet
 * for END_AFTER_QUIET_MS (or after END_MAX_WAIT_MS). Pure timers — unit-tested.
 */
export function createQuietEnder({ isAgentSpeaking, endCall, now = () => Date.now(), setTimer = setTimeout }) {
  let started = null;
  let spoke = false;
  let quietSince = null;
  let done = false;
  const poll = () => {
    if (done) return;
    const t = now();
    if (isAgentSpeaking()) { spoke = true; quietSince = null; } else if (quietSince === null) quietSince = t;
    if ((spoke && quietSince !== null && t - quietSince >= END_AFTER_QUIET_MS) || t - started >= END_MAX_WAIT_MS) {
      done = true;
      endCall('completed');
      return;
    }
    setTimer(poll, 250);
  };
  return {
    start() { if (started === null) { started = now(); setTimer(poll, 250); } },
    stop() { done = true; },
  };
}

// ── Call screen (Figma frame 01) ─────────────────────────────────────────────────────────────────────────────

export const AVATAR_SRC = '/jessica.jpg';
const MY_JOBS = { pathname: '/jobs', state: {} };

/** Speaking / listening visuals. Reads the speaking ref on a timer and re-renders only itself — never the
 *  memoised ConversationManager (a re-render there would rebuild the ElevenLabs connection). */
export function JessicaStage({ isSpeakingRef, connected, compact = false, children }) {
  const [speaking, setSpeaking] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setSpeaking(!!isSpeakingRef.current), 150);
    return () => clearInterval(id);
  }, [isSpeakingRef]);
  const bars = [10, 22, 34, 18, 40, 26, 14, 30, 20, 8];
  if (compact) {                                   // the call screen's header: photo + rings, state, timer / controls
    return (
      <div className="shrink-0 rounded-2xl bg-gradient-to-br from-[#1b1f3b] to-[#2b2f6b] text-white px-4 py-3 flex items-center gap-4" data-testid="jessica-stage">
        <style>{`@keyframes q5ring{0%{transform:scale(1);opacity:.9}100%{transform:scale(1.25);opacity:0}}
          @keyframes q5bar{0%,100%{transform:scaleY(.35)}50%{transform:scaleY(1)}}`}</style>
        <div className="relative w-16 h-16 shrink-0 flex items-center justify-center">
          {[0, 5, 10].map((inset, i) => (
            <span key={inset} className="absolute rounded-full border-2 border-brand-teal"
              style={{ inset, opacity: speaking ? 0.9 - i * 0.2 : 0.35 - i * 0.08,
                animation: speaking ? `q5ring 1.6s ease-out ${i * 0.35}s infinite` : 'none' }} />
          ))}
          <div className="absolute inset-[13px] rounded-full overflow-hidden border-2 border-white bg-brand-dark flex items-center justify-center">
            {imgOk
              ? <img src={AVATAR_SRC} alt="Jessica, AI intake assistant" className="w-full h-full object-cover" onError={() => setImgOk(false)} />
              : <span className="text-lg font-black">J</span>}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-black leading-tight">Jessica <span className="ml-1 px-2 py-0.5 rounded-full bg-white/10 text-[10px] font-semibold align-middle" title="Jessica is an AI assistant, not a person">AI intake assistant</span></p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-[3px] h-4" aria-hidden="true">
              {bars.slice(0, 6).map((h, i) => (
                <span key={i} className="w-[3px] rounded-full bg-brand-teal" style={{ height: Math.round(h / 2.5),
                  animation: speaking ? `q5bar ${0.7 + (i % 3) * 0.15}s ease-in-out ${i * 0.06}s infinite` : 'none', opacity: speaking ? 1 : 0.4 }} />
              ))}
            </div>
            <p className="text-xs" data-testid="speaking-state">{!connected ? 'Connecting…' : speaking ? 'Speaking…' : 'Listening…'}</p>
          </div>
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className="relative rounded-3xl bg-gradient-to-br from-[#1b1f3b] to-[#2b2f6b] h-[460px] lg:h-[560px] flex flex-col items-center justify-center text-white overflow-hidden" data-testid="jessica-stage">
      <style>{`@keyframes q5ring{0%{transform:scale(1);opacity:.9}100%{transform:scale(1.18);opacity:0}}
        @keyframes q5bar{0%,100%{transform:scaleY(.35)}50%{transform:scaleY(1)}}`}</style>
      <div className="relative w-[220px] h-[220px] flex items-center justify-center">
        {[0, 15, 29].map((inset, i) => (
          <span key={inset} className="absolute rounded-full border-2 border-brand-teal"
            style={{ inset, opacity: speaking ? 0.9 - i * 0.2 : 0.35 - i * 0.08,
              animation: speaking ? `q5ring 1.6s ease-out ${i * 0.35}s infinite` : 'none' }} />
        ))}
        <div className="absolute inset-[40px] rounded-full overflow-hidden border-4 border-white bg-brand-dark flex items-center justify-center">
          {imgOk
            ? <img src={AVATAR_SRC} alt="Jessica, AI intake assistant" className="w-full h-full object-cover" onError={() => setImgOk(false)} />
            : <span className="text-4xl font-black">J</span>}
        </div>
      </div>
      <p className="mt-5 text-2xl font-black">Jessica</p>
      <span className="mt-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold" title="Jessica is an AI assistant, not a person">AI intake assistant</span>
      <div className="mt-4 flex items-center gap-[5px] h-10" aria-hidden="true">
        {bars.map((h, i) => (
          <span key={i} className="w-[5px] rounded-full bg-brand-teal" style={{ height: h,
            animation: speaking ? `q5bar ${0.7 + (i % 3) * 0.15}s ease-in-out ${i * 0.06}s infinite` : 'none', opacity: speaking ? 1 : 0.4 }} />
        ))}
      </div>
      <p className="mt-3 text-sm" data-testid="speaking-state">{!connected ? 'Connecting…' : speaking ? 'Speaking…' : 'Listening…'}</p>
    </div>
  );
}

export function checklistState(checklist, { covered = [], answers = {}, recapDone = false }) {
  const done = (item) => (item.kind === 'recap' ? recapDone : item.kind === 'call'
    ? (answers[item.key] || {}).status === 'answered' : covered.includes(item.key));
  const items = (checklist || []).map((it) => ({ ...it, done: done(it) }));
  const now = items.find((it) => !it.done);
  return { items: items.map((it) => ({ ...it, now: it === now })), doneCount: items.filter((it) => it.done).length };
}

export function talentState(sources, answers) {
  const tc = ((answers.target_companies || {}).value) || {};
  const split = (t) => String(t || '').split(/[,;\n]+/).map((x) => x.trim()).filter((x) => x && x.toLowerCase() !== 'none');
  const targets = split(tc.target).map((x) => x.toLowerCase());
  return {
    suggestions: (sources || []).map((s) => ({ ...s, confirmed: targets.some((t) => t.includes(s.name.toLowerCase()) || s.name.toLowerCase().includes(t)) })),
    added: split(tc.target).filter((t) => !(sources || []).some((s) => s.name.toLowerCase() === t.toLowerCase())),
    avoid: split(tc.avoid),
  };
}

function SidePanel({ title, right, children, testid, grow = false }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-col ${grow ? 'flex-1 min-h-0' : 'shrink-0'}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3 shrink-0">
        <h3 className="text-base font-bold text-gray-900">{title}</h3>{right}
      </div>
      <div className={grow ? 'flex-1 min-h-0 overflow-y-auto pr-1' : ''}>{children}</div>
    </div>
  );
}

function LiveCall({ intakeId, session, jobTitle, spec, answers, initialAnswers, onFieldSaved, onEnded }) {
  const navigate = useNavigate();
  const [callState, setCallState] = useState('connecting');
  const [error, setError] = useState('');
  const [captions, setCaptions] = useState([]);
  const [talentSources, setTalentSources] = useState(session.talent_sources || []);
  const [talentNote, setTalentNote] = useState(session.talent_note || null);
  const [talentDelayed, setTalentDelayed] = useState(false);
  const transcriptEndRef = useRef(null);
  useEffect(() => { transcriptEndRef.current?.scrollIntoView?.({ block: 'end' }); }, [captions.length]);
  const [notes, setNotes] = useState(session.notes || []);
  const [covered, setCovered] = useState(session.covered || []);
  const conversationRef = useRef(null);
  const isSpeakingRef = useRef(false);
  const conversationIdRef = useRef(null);
  const connectedAtRef = useRef(null);
  const endingRef = useRef(false);
  const micStreamRef = useRef(null);
  const turnHoldRef = useRef(null);
  const enderRef = useRef(null);
  const timerDisplayRef = useRef(null);
  const handleEndRef = useRef(null);
  const onFieldSavedRef = useRef(onFieldSaved);
  onFieldSavedRef.current = onFieldSaved;

  const elapsedSeconds = () => (connectedAtRef.current ? Math.round((Date.now() - connectedAtRef.current) / 1000) : 0);

  const saveConversationId = (id) => {
    if (!id || conversationIdRef.current === id) return;
    conversationIdRef.current = id;
    intakeApi(`/${intakeId}/conversation`, { method: 'PATCH', body: { conversation_id: id } }).catch(() => {});
  };

  const configRef = useRef(null);
  if (!configRef.current) {
    configRef.current = {
      clientTools: {
        update_field: async ({ field_key, value, display_text }) => {
          try {
            const res = await intakeApi(`/${intakeId}/fields/${field_key}`, {
              method: 'PUT', body: { status: 'answered', value, source: 'voice' },
            });
            onFieldSavedRef.current?.(res);
            return `Saved: ${res.display_text || display_text}${res.market_note || ''}`;
          } catch (e) {
            return String(e.message || '').startsWith('Not saved') ? e.message : `Not saved: ${e.message}`;
          }
        },
        request_finish: async ({ topics_covered, still_tbd }) => {
          let res = null;
          try {
            res = await intakeApi(`/${intakeId}/finish-check`, {
              method: 'POST', body: { topics_covered: topics_covered || '', still_tbd: still_tbd || '', elapsed_seconds: elapsedSeconds() },
            });
          } catch (e) { res = null; }
          // Fail open (as Phase 3): if the server can't answer, Jessica may close; the job then stays resumable
          if (!res || res.allow) {
            enderRef.current?.start();
            return (res && res.message) || 'Finish approved. Thank the hiring manager and say goodbye.';
          }
          return res.message;
        },
        // Background tool: a deep-dive topic is done → tick it and show the note ("Captured so far")
        save_note: async ({ topic, summary }) => {
          try {
            const res = await intakeApi(`/${intakeId}/notes`, { method: 'POST', body: { topic, summary: summary || '' } });
            setCovered(res.covered || []);
            if (res.summary) setNotes((n) => [...n.filter((x) => x.topic !== res.topic), { topic: res.topic, summary: res.summary }]);
          } catch (e) { /* never interrupt the call */ }
          return 'noted';
        },
      },
      onConnect: ({ conversationId }) => {
        connectedAtRef.current = connectedAtRef.current || Date.now();
        setCallState('connected');
        saveConversationId(conversationId);
      },
      onDisconnect: (details) => {
        if (endingRef.current) return;
        // Refused before the call was established (e.g. the account's concurrent-call limit) → 'busy'
        const neverConnected = !conversationIdRef.current && connectedAtRef.current === null;
        handleEndRef.current?.(neverConnected && isBusyError(details) ? 'busy' : 'disconnected');
      },
      onError: () => { if (!endingRef.current) setError('The connection to Jessica had a problem.'); },
      onMessage: (msg) => {
        if (!msg || !msg.message) return;
        setCaptions((c) => [...c, { who: msg.source === 'user' ? 'you' : 'jessica', text: msg.message }].slice(-400));
      },
    };
  }
  if (!enderRef.current) {
    enderRef.current = createQuietEnder({
      isAgentSpeaking: () => !!isSpeakingRef.current,
      endCall: (reason) => handleEndRef.current?.(reason),
    });
  }

  handleEndRef.current = async (reason, note) => {
    if (endingRef.current) return;
    endingRef.current = true;
    enderRef.current?.stop();
    try { turnHoldRef.current?.stop(); } catch (e) { /* ignore */ }
    try { await conversationRef.current?.endSession(); } catch (e) { /* ignore */ }
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    let result = null;
    try {
      result = await intakeApi(`/${intakeId}/end`, {
        method: 'POST', body: { reason, conversation_id: conversationIdRef.current || undefined },
      });
    } catch (e) { result = { status: 'call_paused' }; }
    onEnded(result, reason, note);
  };

  // Start the session once (mic first — the turn hold listens to it)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (cancelled) return;
        const conv = conversationRef.current;
        if (!conv) throw new Error('Conversation not ready');
        const id = await conv.startSession({ signedUrl: session.signed_url });
        saveConversationId(id);
      } catch (e) {
        if (cancelled) return;
        // Never connected: end the segment so the server undoes it and deletes the unused session agent
        const busy = isBusyError(e);
        handleEndRef.current?.(busy ? 'busy' : 'error', busy ? BUSY_MESSAGE
          : String(e.message || '').toLowerCase().includes('permission') ? 'Microphone access was denied.'
            : 'Unable to start the call. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Turn hold (bundle K): while the HM is speaking, tell ElevenLabs they're active so Jessica waits
  useEffect(() => {
    if (callState !== 'connected' || turnHoldRef.current) return;
    turnHoldRef.current = startTurnHold({
      stream: micStreamRef.current,
      send: () => conversationRef.current?.sendUserActivity?.(),
      isAgentSpeaking: () => !!isSpeakingRef.current,
    });
  }, [callState]);

  // Talent sources still being looked up when the call started: poll, then tell Jessica (once) and show them
  useEffect(() => {
    if (callState !== 'connected' || (session.talent_sources || []).length) return undefined;
    let polls = 0;
    const id = setInterval(async () => {
      polls += 1;
      if (polls > TALENT_POLL_MAX) { clearInterval(id); return; }
      try {
        const res = await intakeApi(`/${intakeId}/talent-sources`);
        if (res.status === 'ready' && (res.talent_sources || []).length) {
          clearInterval(id);
          setTalentSources(res.talent_sources);
          setTalentNote(res.talent_note || null);
          setTalentDelayed(false);
          try { conversationRef.current?.sendContextualUpdate(res.message); } catch (e) { /* ignore */ }
        } else if (['skipped', 'failed'].includes(res.status)) {
          clearInterval(id);
        } else {
          setTalentDelayed(res.status === 'delayed');   // keep polling: it may still arrive during the call
        }
      } catch (e) { /* keep trying until the limit */ }
    }, TALENT_POLL_MS);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  // Timer (DOM only — no state updates) + one wrap-up note at 15 minutes
  useEffect(() => {
    if (callState !== 'connected') return undefined;
    let wrapSent = false;
    const id = setInterval(() => {
      const s = elapsedSeconds();
      if (timerDisplayRef.current) timerDisplayRef.current.textContent = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
      if (!wrapSent && s >= WRAP_UP_AFTER_S) {
        wrapSent = true;
        try { conversationRef.current?.sendContextualUpdate(WRAP_UP_NOTE); } catch (e) { /* ignore */ }
      }
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  useEffect(() => () => {
    try { turnHoldRef.current?.stop(); } catch (e) { /* ignore */ }
    try { enderRef.current?.stop(); } catch (e) { /* ignore */ }
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
  }, []);

  const recapDone = captions.some((c) => c.who === 'jessica');                 // Jessica has opened with the recap
  const { items, doneCount } = checklistState(session.checklist, { covered, answers, recapDone });
  const talent = talentState(talentSources, answers);
  const labels = Object.fromEntries((session.checklist || []).map((c) => [c.key, c.label]));
  // Form answers changed during the call (Jessica's update_field): a TBD answered, or an answer changed (e.g. the pay range)
  const changedOnCall = (spec.fields || []).filter((f) => !f.call_field && (answers[f.key] || {}).status === 'answered'
    && (answers[f.key] || {}).display_text !== (initialAnswers[f.key] || {}).display_text)
    .map((f) => ({ field: f, wasTbd: (initialAnswers[f.key] || {}).status === 'tbd', before: (initialAnswers[f.key] || {}).display_text }));

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50 font-inter" data-testid="call-page">
      <ConversationManager config={configRef.current} conversationRef={conversationRef} isSpeakingRef={isSpeakingRef} />
      <header className="bg-white border-b border-gray-200 shrink-0">
        <div className="max-w-[1600px] mx-auto px-6 py-2.5 flex items-center gap-4">
          <button onClick={async () => { await handleEndRef.current('paused'); navigate(MY_JOBS.pathname, { state: MY_JOBS.state }); }}
            className="text-xs text-gray-500 hover:text-brand-blue">← My jobs</button>
          <h1 className="text-lg font-black text-gray-900 truncate">{jobTitle || 'New job'}</h1>
        </div>
      </header>
      <main className="flex-1 min-h-0 w-full max-w-[1600px] mx-auto px-6 py-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)_minmax(0,1fr)] gap-5">
        <section className="min-h-0 flex flex-col gap-3" data-testid="col-call">
          <JessicaStage compact isSpeakingRef={isSpeakingRef} connected={callState === 'connected'}>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <span className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 text-xs font-semibold" data-testid="live-pill">
                <span className={`w-2 h-2 rounded-full ${callState === 'connected' ? 'bg-green-400 animate-pulse' : 'bg-gray-300'}`} />
                {callState === 'connected' ? 'Live with Jessica' : 'Connecting'} · <span ref={timerDisplayRef}>00:00</span>
              </span>
              <div className="flex gap-2">
                <button onClick={() => handleEndRef.current('paused')} className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-xs font-semibold">Pause</button>
                <button onClick={() => handleEndRef.current('completed')} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold">End call</button>
              </div>
            </div>
          </JessicaStage>
          {error && <p className="text-sm text-red-600 shrink-0">{error}</p>}
          <div className="flex-1 min-h-0 bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-col">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2 shrink-0">Live transcript</p>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-1.5" data-testid="captions" aria-live="polite">
              {captions.length === 0 && <p className="text-sm text-gray-400">The conversation appears here as you talk.</p>}
              {captions.map((c, i) => (
                <p key={i} className="text-sm text-gray-800">
                  <span className={`font-bold mr-2 ${c.who === 'jessica' ? 'text-brand-blue' : 'text-gray-500'}`}>{c.who === 'jessica' ? 'Jessica' : 'You'}</span>{c.text}
                </p>
              ))}
              <div ref={transcriptEndRef} />
            </div>
          </div>
        </section>

        <section className="min-h-0 flex flex-col gap-4" data-testid="col-topics">
          <SidePanel grow title="Conversation topics" testid="topics" right={<span className="text-xs text-gray-500">{doneCount} of {items.length}</span>}>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
              <div className="h-full bg-brand-teal" style={{ width: `${items.length ? Math.round((doneCount / items.length) * 100) : 0}%` }} />
            </div>
            <ul className="space-y-2.5">
              {items.map((it) => (
                <li key={it.key} className="flex items-center gap-2.5 text-sm" data-testid={`topic-${it.key}`}>
                  <span className={`w-[18px] h-[18px] rounded-full flex-shrink-0 ${it.done ? 'bg-brand-green' : it.now ? 'border-2 border-brand-blue' : 'border-2 border-gray-200'}`} />
                  <span className={it.done ? 'text-gray-800' : it.now ? 'font-bold text-gray-900' : 'text-gray-400'}>{it.label}</span>
                  {it.now && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-brand-blue text-[11px] font-bold">Now</span>}
                </li>
              ))}
            </ul>
          </SidePanel>
          <SidePanel title="Suggested talent sources" testid="talent">
            {talentNote && <p className="text-xs text-gray-500 mb-2" data-testid="talent-note">{talentNote}</p>}
            {!talent.suggestions.length && !talent.added.length && <p className="text-xs text-gray-400">Jessica will ask which companies to recruit from.</p>}
            {talentDelayed && !talent.suggestions.length && <p className="text-xs text-amber-700 mt-1" data-testid="talent-delayed">Company suggestions are delayed — they'll be added to the recruiter brief afterwards.</p>}
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
              {talent.suggestions.map((s) => (
                <span key={s.name} title={s.why} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.confirmed ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                  {s.name}{s.confirmed ? ' ✓' : ''}
                </span>
              ))}
              {talent.added.map((t) => <span key={t} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700">{t} ✓</span>)}
            </div>
            {talent.avoid.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-xs font-bold text-red-600">Avoid:</span>
                {talent.avoid.map((a) => <span key={a} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">{a}</span>)}
              </div>
            )}
          </SidePanel>
        </section>

        <section className="min-h-0 flex flex-col" data-testid="col-captured">
          <SidePanel grow title="Captured so far" testid="captured">
            {!notes.length && !changedOnCall.length && <p className="text-xs text-gray-400">Notes appear as Jessica covers each topic.</p>}
            <div className="space-y-3">
              {changedOnCall.map(({ field: f, wasTbd, before }) => (
                <div key={f.key} data-testid={`changed-${f.key}`}>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{wasTbd || !before ? 'Previously TBD → now answered' : 'Changed on the call'}</p>
                  <p className="text-sm text-gray-800">{f.label}: {answers[f.key].display_text}</p>
                  {!wasTbd && before && <p className="text-xs text-gray-400 line-through">{before}</p>}
                </div>
              ))}
              {notes.map((n) => (
                <div key={n.topic}>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{labels[n.topic] || (String(n.topic).startsWith('check:') ? 'Market check' : n.topic)}</p>
                  <p className="text-sm text-gray-800">{n.summary}</p>
                </div>
              ))}
            </div>
          </SidePanel>
        </section>
      </main>
    </div>
  );
}


function JobIntakeCallPage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();
  const [spec, setSpec] = useState(null);
  const [job, setJob] = useState(null);
  const [answers, setAnswers] = useState({});
  const [initialAnswers, setInitialAnswers] = useState({});
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState('idle');          // idle | starting | live | ended
  const [ended, setEnded] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [fields, data] = await Promise.all([intakeApi('/fields'), intakeApi(`/${intakeId}`)]);
        setSpec(fields);
        setJob(data);
        setAnswers(data.answers || {});
        setInitialAnswers(data.answers || {});
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [intakeId]);

  const start = async () => {
    setPhase('starting'); setError('');
    try {
      setSession(await intakeApi(`/${intakeId}/session`, { method: 'POST' }));
      setPhase('live');
    } catch (e) {
      setError(e.message);
      setPhase('idle');
    }
  };

  const onFieldSaved = (res) => {
    setAnswers((a) => ({ ...a, [res.field_key]: { status: res.status, value: res.value, display_text: res.display_text, source: 'voice' } }));
  };
  const onEnded = (result, reason, note) => {
    setJob((j) => ({ ...j, status: result.status }));
    if (result.connected === false) {                // never reached Jessica: back to the start screen + Retry
      setSession(null);
      setError(result.busy || reason === 'busy' ? BUSY_MESSAGE : (note || 'Unable to start the call. Please try again.'));
      setPhase('idle');
      return;
    }
    setEnded(result);
    setPhase('ended');
  };

  if (error && !job) return <div className="p-10 text-center text-red-600">{error}</div>;
  if (!spec || !job) return <div className="p-10 text-center text-gray-500">Loading…</div>;

  if (phase === 'live' && session) {
    return <LiveCall intakeId={intakeId} session={session} jobTitle={job.job_title} spec={spec} answers={answers}
      initialAnswers={initialAnswers} onFieldSaved={onFieldSaved} onEnded={onEnded} />;
  }

  const resuming = (job.segments || []).length > 0;
  const canCall = ['form_done', 'call_in_progress', 'call_paused'].includes(job.status);

  return (
    <div className="min-h-screen bg-gray-50 font-inter">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-8 py-4">
          <button onClick={() => navigate(MY_JOBS.pathname, { state: MY_JOBS.state })} className="text-xs text-gray-500 hover:text-brand-blue">← My jobs</button>
          <h1 className="text-xl font-black text-gray-900">{job.job_title || 'New job'}</h1>
          <p className="text-xs text-gray-500">{STATUS_LABELS[job.status] || job.status}</p>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-8 py-10">
        {phase !== 'ended' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 flex flex-col sm:flex-row gap-6 items-center">
            <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-brand-teal/40 flex-shrink-0 bg-brand-dark">
              <img src={AVATAR_SRC} alt="Jessica, AI intake assistant" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900">{resuming ? 'Resume your call with Jessica' : 'Talk the role through with Jessica'}</h2>
              <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-blue-50 text-brand-blue text-[11px] font-bold">AI intake assistant</span>
              <p className="text-sm text-gray-600 mt-2">
                Jessica has your form. She'll clarify anything marked TBD, ask about target companies and knock-outs,
                then the project, the first 90 days, the team and what makes a great hire. About 10–15 minutes.
              </p>
              {error && (
                <p role="alert" data-testid="call-notice"
                  className={`mt-3 text-sm ${error === BUSY_MESSAGE ? 'text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2' : 'text-red-600'}`}>
                  {error}{error === BUSY_MESSAGE ? ' Your form and answers are saved.' : ''}
                </p>
              )}
              {canCall ? (
                <button onClick={start} disabled={phase === 'starting'}
                  className="mt-5 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold disabled:opacity-50">
                  {phase === 'starting' ? 'Connecting…' : error ? 'Retry' : resuming ? 'Resume call' : 'Start call'}
                </button>
              ) : (
                <button onClick={() => navigate(`/jobs/intake/${intakeId}`)}
                  className="mt-5 px-5 py-2.5 rounded-xl border border-brand-blue text-brand-blue text-sm font-bold">Back to the form</button>
              )}
            </div>
          </div>
        )}
        {phase === 'ended' && ended && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8" data-testid="call-ended">
            {ended.status === 'call_paused' ? (
              <>
                <h2 className="text-lg font-black text-gray-900">Call paused</h2>
                <p className="text-sm text-gray-600 mt-2">Everything so far is saved. Resume any time — Jessica will pick up where you left off.</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button onClick={() => { setPhase('idle'); setSession(null); setJob((j) => ({ ...j, segments: [...(j.segments || []), {}] })); }}
                    className="px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold">Resume now</button>
                  <button onClick={() => navigate(`/jobs/intake/${intakeId}/review`)}
                    className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold">Skip the rest and generate</button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-black text-gray-900">Thanks — the call is complete</h2>
                <p className="text-sm text-gray-600 mt-2">Your job description and screening questions are being prepared for your review.</p>
                <button onClick={() => navigate(`/jobs/intake/${intakeId}/review`)}
                  className="mt-5 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold">Open review →</button>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default JobIntakeCallPage;
